import React, { FC } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useToastErrorMessage } from '@/utils/Messages';

import { putAddress } from '@/services/Address/request';
import { putGuest } from '@/services/Guest/request';
import { Property, PropertyGrouped } from '@/services/Property/types';

import { useEditForm } from '@/context/EditFormContext';
import { useLoader } from '@/context/LoaderContext';
import { useReservation } from '@/context/ReservationContext';
import { useToast } from '@/context/ToastContext';

import { useReservationForm } from '@/hooks/ReservationHook/useReservationForm';

import FormGridRow from '@/components/FormGridRow';
import { FormAddress, FormButton, TextField } from '@/components/index';

import { ButtonsContainer, FormContainer, FormContent } from './styles';

interface Props {
  email: string;
  phone1: string;
  street: string;
  zipCode: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  country: string;
  addressExists: boolean;
  activeButton: (a: boolean) => void;
}

const ContactDataForm: FC<Props> = ({
  email,
  phone1,
  street,
  zipCode,
  number,
  neighborhood,
  city,
  state,
  country,
  addressExists,
  activeButton,
}) => {
  const { reservationData, setReservationData } = useReservation();
  const propertyId = reservationData?.listing?.property?.id;
  const { dataGrouped, setDataGrouped } = useReservationForm();
  const toast = useToast();
  const { setLoad } = useLoader();
  const toastErrorRequest = useToastErrorMessage();
  const { setOpenForm, setFormActive } = useEditForm();

  const handleCloseEditForm = () => {
    setOpenForm(false);
    setFormActive(0);
    activeButton(false);
  };
  const validation = Yup.object().shape({
    email: Yup.string().required().email('E-mail inválido'),
    phone1: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      email,
      phone1,
      street,
      zipCode,
      number,
      neighborhood,
      city,
      state,
      country,
    },
    validationSchema: validation,
    onSubmit: async (values) => {
      setLoad(true);
      try {
        const mainAddress = {
          ...reservationData?.guest?.user?.main_address,
          street: values.street,
          number: values.number,
          neighborhood: values.neighborhood,
          city: values.city,
          state: values.state,
          postal_code: values.zipCode,
          country: values.country,
        };

        const guest = {
          ...reservationData?.guest,
          user: {
            ...reservationData?.guest?.user,
            gender: 'Male',
            email: values.email,
            phone_number1: values.phone1,
            main_address: mainAddress,
          },
        };
        if (guest.user.cpf === reservationData.guest.user.cpf) {
          delete guest.user.cpf;
        }
        if (reservationData.guest.user.cnpj === guest.user.cnpj) {
          delete guest.user.cnpj;
        }
        await putGuest(guest.id, guest);

        if (addressExists) {
          await putAddress(mainAddress.id, mainAddress);
        }
        guest.user.cpf = reservationData.guest.user.cpf;
        const newReservation = {
          ...reservationData,
          guest,
        };
        await setReservationData({ ...newReservation });
        const key = newReservation?.check_in_date;

        if (dataGrouped) {
          setDataGrouped(
            dataGrouped.map((category: PropertyGrouped) => ({
              ...category,
              data: category.data.map<Property>((property) => ({
                ...property,
                reservation:
                  property?.id === propertyId
                    ? { ...property.reservation, [key]: { ...newReservation } }
                    : { ...property.reservation },
              })),
            })),
          );
        }

        toast.success('Os dados de contato foram salvos');
        handleCloseEditForm();
        setLoad(false);
      } catch (e: unknown) {
        if (e instanceof Error) {
          setLoad(false);
          toastErrorRequest(e);
        }
      }
    },
  });

  return (
    <FormContainer
      onContextMenu={(event): void => {
        event.stopPropagation();
      }}
    >
      <FormContent onSubmit={formik.handleSubmit}>
        <FormGridRow>
          <TextField label="E-mail" formik={formik} id="email" value={email} />
        </FormGridRow>
        <FormGridRow>
          <TextField
            label="Telefone/Celular"
            formik={formik}
            placeholder="+00 (00) 00000-0000"
            id="phone1"
            mask="phone"
            value={phone1}
          />
        </FormGridRow>
        <FormAddress
          addressProp={{
            city,
            number,
            street,
            state,
            neighborhood,
            country,
            postal_code: zipCode,
            id: 0,
            complement: '',
          }}
          formik={formik}
        />
        <ButtonsContainer>
          <FormButton type="button" customColor="grey" onClick={() => handleCloseEditForm()}>
            Cancelar
          </FormButton>
          <FormButton onClick={() => formik.handleSubmit}>Salvar</FormButton>
        </ButtonsContainer>
      </FormContent>
    </FormContainer>
  );
};

export default ContactDataForm;
